import { useContext, useEffect, useState } from "react";
import ToastContext from "../../../context/toastProvider";
import MobileFilterSheet from "../../ui/MobileFilterSheet";
import MobileConfirm from "../../ui/MobileConfirm";
import { updateRoles, createGroup, updateGroup, deleteGroup } from "../../../components/access/functions";

const primaryBtn =
  "inline-flex min-h-[44px] flex-1 items-center justify-center rounded-lg bg-primary px-4 text-sm font-medium text-mgs-bg hover:bg-[#b47c06] disabled:opacity-50";
const dangerBtn =
  "inline-flex min-h-[44px] flex-1 items-center justify-center rounded-lg bg-red-700 px-4 text-sm font-medium text-white hover:bg-red-800";
const inputClass =
  "w-full min-h-[44px] rounded-lg border border-primary/50 bg-monao px-3 text-base text-laranjinha";

export function MobileStaffSheet({ open, onClose, staffUser, user, onChanged }) {
  const { goodToast, badToast } = useContext(ToastContext);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isMaster, setIsMaster] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setIsAdmin(Boolean(staffUser?.roles?.includes("admin")));
    setIsMaster(Boolean(staffUser?.roles?.includes("master")));
  }, [staffUser]);

  if (!staffUser) return null;

  const isSelfMaster = staffUser.email === user.email && staffUser.roles?.includes("master");

  const handleSave = async () => {
    setSaving(true);
    const data = await updateRoles(user.token, staffUser._id, { admin: isAdmin, master: isMaster });
    setSaving(false);
    if (data.type === "error") {
      badToast(data.message);
      return;
    }
    goodToast("Acesso atualizado");
    onChanged();
  };

  return (
    <MobileFilterSheet open={open} onClose={onClose} title={staffUser.name}>
      <p className="mb-3 text-sm text-laranjinha/80">{staffUser.email}</p>
      <label className="mb-2 flex min-h-[44px] items-center gap-3">
        <input type="checkbox" checked={isAdmin} onChange={() => setIsAdmin(!isAdmin)} />
        Admin
      </label>
      <label className="mb-4 flex min-h-[44px] items-center gap-3">
        <input type="checkbox" checked={isMaster} onChange={() => setIsMaster(!isMaster)} disabled={isSelfMaster} />
        Master
      </label>
      {isSelfMaster ? (
        <p className="mb-4 text-xs text-laranjinha/70">Você não pode revogar seu próprio acesso master.</p>
      ) : null}
      <button type="button" className={primaryBtn} onClick={handleSave} disabled={saving}>
        Salvar acesso
      </button>
    </MobileFilterSheet>
  );
}

export function MobileGroupSheet({ open, onClose, group, staff, user, onChanged }) {
  const { goodToast, badToast } = useContext(ToastContext);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [memberIds, setMemberIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setName(group?.name || "");
    setDescription(group?.description || "");
    setMemberIds((group?.members || []).map((member) => member._id));
  }, [group, open]);

  const toggleMember = (id) => {
    setMemberIds((prev) => prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      badToast("Insira um nome");
      return;
    }
    setSaving(true);
    let data;
    if (group) {
      data = await updateGroup(user.token, group._id, { name: name.trim(), description: description.trim(), members: memberIds });
    } else {
      data = await createGroup(user.token, { name: name.trim(), description: description.trim() });
    }
    if (data.type === "error") {
      setSaving(false);
      badToast(data.message);
      return;
    }
    // Criação: membros escolhidos entram num segundo passo (PUT) para manter o POST simples.
    if (!group && memberIds.length && data.data?._id) {
      const memberData = await updateGroup(user.token, data.data._id, { members: memberIds });
      setSaving(false);
      if (memberData.type === "error") {
        // O grupo já foi criado no backend (sem membros) — não deixar o usuário achar
        // que nada foi salvo. Fecha o sheet e atualiza a lista para refletir o estado
        // real; o usuário completa os membros editando o grupo em seguida.
        badToast(`Grupo criado, mas houve erro ao salvar os membros: ${memberData.message}`);
        onChanged();
        return;
      }
      goodToast("Grupo criado");
      onChanged();
      return;
    }
    setSaving(false);
    goodToast(group ? "Grupo atualizado" : "Grupo criado");
    onChanged();
  };

  const handleDelete = async () => {
    setConfirmDelete(false);
    const data = await deleteGroup(user.token, group._id);
    if (data.type === "error") {
      badToast(data.message);
      return;
    }
    goodToast("Grupo removido");
    onChanged();
  };

  return (
    <MobileFilterSheet open={open} onClose={onClose} title={group ? "Editar grupo" : "Criar grupo"}>
      <label className="mb-1 block text-sm" htmlFor="mobile-group-name">Nome</label>
      <input id="mobile-group-name" className={`${inputClass} mb-3`} value={name} onChange={(e) => setName(e.target.value)} />
      <label className="mb-1 block text-sm" htmlFor="mobile-group-description">Descrição</label>
      <input id="mobile-group-description" className={`${inputClass} mb-4`} value={description} onChange={(e) => setDescription(e.target.value)} />
      <p className="mb-2 text-sm">Membros (somente staff)</p>
      <ul className="mb-4 max-h-56 overflow-y-auto">
        {staff.map((member) => (
          <li key={member._id}>
            <label className="flex min-h-[44px] items-center gap-3">
              <input type="checkbox" checked={memberIds.includes(member._id)} onChange={() => toggleMember(member._id)} />
              {member.name}
            </label>
          </li>
        ))}
      </ul>
      <div className="flex gap-3">
        {group ? (
          <button type="button" className={dangerBtn} onClick={() => setConfirmDelete(true)}>Excluir</button>
        ) : null}
        <button type="button" className={primaryBtn} onClick={handleSave} disabled={saving}>Salvar grupo</button>
      </div>
      <MobileConfirm
        open={confirmDelete}
        title="Excluir grupo"
        message={`Excluir o grupo "${group?.name}"?`}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
      />
    </MobileFilterSheet>
  );
}
